import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLFieldSetElement> {
  static targets = ["keyButton", "keyFilterInput", "keyListPopover", "popoverTrigger", "noResults"];

  declare keyButtonTargets: HTMLButtonElement[];
  declare keyFilterInputTarget?: HTMLInputElement;
  declare keyListPopoverTarget: HTMLDivElement;
  declare popoverTriggerTarget: HTMLButtonElement;
  declare noResultsTarget: HTMLDivElement;

  declare fieldElement: HTMLTextAreaElement | null;

  declare triggeredInLine: boolean;

  connect(): void {
    this.fieldElement = this.element.querySelector("textarea, input[type=text]");

    if (!this.fieldElement) {
      throw new Error("Could not find a textarea or input[type=text] element.");
    }

    this.fieldElement.oninput = this.input.bind(this);

    // We set onkeydown listener when the popover is opened, to handle the keyboard navigation.
    // We clear that handler when the variable is selected, or when user press "Escape" or "Backspace" while inside the popover.
    // But if the user close the popover by clicking outside, we don't want that keydown listener to remain active in the textarea.
    // So we clear it when the textarea is focused again.
    //
    // Ideally, we should add this to the popover.onblur() event, but popover is rendered by React and we don't have access to that event.
    this.fieldElement.onfocus = () => {
      this.element.onkeydown = null;
    };
  }

  // Handle typing in the textarea to trigger inline suggestions
  input(): void {
    if (!this.fieldElement) {
      return;
    }

    const cursorPosition = this.fieldElement.selectionStart;
    const textBeforeCursor = this.fieldElement.value.substring(0, cursorPosition);

    const match = textBeforeCursor.match(/.*{{$/);
    if (match) {
      this.triggeredInLine = true;
      this.openKeyListPopover();
    }
  }

  // Handle clicking the `{} Insert Variable` button
  clickInsertVariable(): void {
    this.triggeredInLine = false;
    this.openKeyListPopover();
  }

  // Open the key list popover near the cursor position
  openKeyListPopover(): void {
    this.registerKeydownListener();
    this.movePopoverTriggerNearCursor();
    this.popoverTriggerTarget.click();
  }

  // Handle clicking a variable key in the popover
  clickKey(event: Event): void {
    event.preventDefault();

    const key = (event.currentTarget as HTMLButtonElement).dataset.key;
    if (!key) {
      return;
    }

    this.insertVariable(key);
  }

  // Filter available keys based on user input in the popover's search field
  filterKeys(event: Event): void {
    const query = (event.currentTarget as HTMLInputElement).value;
    const keys = this.keyListPopoverTarget.querySelectorAll("button");

    let foundKeys = 0;
    keys.forEach((key) => {
      if (key.textContent && key.textContent.toLowerCase().includes(query)) {
        key.classList.remove("hidden");
        foundKeys += 1;
      } else {
        key.classList.add("hidden");
      }
    });

    if (foundKeys === 0) {
      this.noResultsTarget.classList.remove("hidden");
    } else {
      this.noResultsTarget.classList.add("hidden");
    }
  }

  private registerKeydownListener(): void {
    this.element.onkeydown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          this.fieldElement?.focus();
          this.element.onkeydown = null;
          break;
        case "ArrowDown":
          event.preventDefault();

          if (event.target instanceof HTMLInputElement) {
            // If we are on the key filter input, move to the first visible key button
            const firstVisibleKey = this.keyListPopoverTarget.querySelector("button:not(.hidden)");
            if (firstVisibleKey) {
              (firstVisibleKey as HTMLButtonElement).focus();
            }
          } else if (event.target instanceof HTMLButtonElement) {
            // If we are on a key button, move to the next visible key button
            const buttons = Array.from(
              this.keyListPopoverTarget.querySelectorAll("button:not(.hidden)"),
            );
            const currentIndex = buttons.indexOf(event.target);
            if (currentIndex >= 0 && currentIndex < buttons.length - 1) {
              (buttons[currentIndex + 1] as HTMLButtonElement).focus();
            }
          }
          break;
        case "ArrowUp":
          event.preventDefault();

          if (event.target instanceof HTMLButtonElement) {
            const buttons = Array.from(
              this.keyListPopoverTarget.querySelectorAll("button:not(.hidden)"),
            );
            const currentIndex = buttons.indexOf(event.target);
            if (currentIndex > 0) {
              // If there is a previous button, move to it
              (buttons[currentIndex - 1] as HTMLButtonElement).focus();
            } else {
              // Otherwise, move to the filter input
              this.keyFilterInputTarget?.focus();
            }
          }
          break;
        case "Backspace":
          if (event.target instanceof HTMLInputElement) {
            if (event.target.value === "") {
              // If we are on the filter input and it's empty, move to the textarea
              event.preventDefault();
              this.fieldElement?.focus();
              this.element.onkeydown = null;
            }
          } else {
            event.preventDefault();
            // If we are on a button and Backspace is pressed, move to the filter input
            this.keyFilterInputTarget?.focus();
          }

          break;
        case "Enter":
          if (event.target instanceof HTMLInputElement) {
            const firstVisibleKey = this.keyListPopoverTarget.querySelector("button:not(.hidden)");
            // If the filter input is empty or there are no visible keys, do nothing.
            // Otherwise, the default behavior will click on the first visible key button.
            if (!firstVisibleKey || event.target.value === "") {
              event.preventDefault();
              return;
            }
          }
          break;
      }
    };
  }

  // Insert the selected variable key into the textarea at the cursor position
  private insertVariable(key: string): void {
    if (!this.fieldElement) {
      return;
    }

    const cursorPosition = this.fieldElement.selectionStart;
    const textBeforeCursor = this.fieldElement.value.substring(0, cursorPosition);
    const textAfterCursor = this.fieldElement.value.substring(cursorPosition);

    let newContent;
    let newCursorPosition;

    const padding = 6; // Length of `{{  }}`

    if (this.triggeredInLine) {
      // If triggered inline by typing `{{`, replace from there
      const lastOpenBracePosition = textBeforeCursor.lastIndexOf("{{");
      newContent =
        textBeforeCursor.substring(0, lastOpenBracePosition) + `{{ ${key} }}` + textAfterCursor;
      newCursorPosition = lastOpenBracePosition + key.length + padding;
    } else {
      // Otherwise, just insert at the cursor position
      newContent = textBeforeCursor + `{{ ${key} }}` + textAfterCursor;
      newCursorPosition = cursorPosition + key.length + padding;
    }

    this.fieldElement.value = newContent;
    this.fieldElement.selectionEnd = newCursorPosition;
    this.fieldElement.focus();
    // deregister the keydown listener as we're back in the textarea
    this.element.onkeydown = null;
  }

  // Move the popover trigger button near the cursor position in the textarea,
  // so we can use it to anchor the popover.
  private movePopoverTriggerNearCursor(): void {
    const { left, top } = this.getCursorPosition();
    this.popoverTriggerTarget.style.left = `${left}px`;
    // There is no science behind the `-22` here, just trial and error to get it to look right.
    this.popoverTriggerTarget.style.top = `${top - 22}px`;
  }

  // Calculate the cursor position within the textarea
  // by replicating its content and style in a temporary div,
  // then determining the position of a span placed after the temporary div.
  private getCursorPosition(): { left: number; top: number } {
    if (!this.fieldElement) {
      return { left: 0, top: 0 };
    }

    const div = document.createElement("div");
    const textAreaStyles = getComputedStyle(this.fieldElement);
    Array.from(textAreaStyles).forEach((key) => {
      div.style.setProperty(key, textAreaStyles.getPropertyValue(key));
    });
    div.style.setProperty("position", "relative");
    div.textContent = this.fieldElement.value.substring(0, this.fieldElement.selectionStart);

    const span = document.createElement("span");
    div.appendChild(span);

    const { offsetLeft: textAreaLeft, offsetTop: textAreaTop } = this.fieldElement;

    document.body.appendChild(div);
    const { offsetLeft: spanLeft, offsetTop: spanTop } = span;
    document.body.removeChild(div);

    return { left: spanLeft + textAreaLeft, top: spanTop + textAreaTop };
  }
}
